import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ColumnDef, FilterDef, SortState, FilterState } from './types.js';
import { sortRows } from './sorting.js';
import { filterRows } from './filtering.js';

const SORT_KEY_PARAM = 'sortBy';
const SORT_DIR_PARAM = 'sortDir';
const FILTER_PREFIX = 'f_';
const COLLAPSED_GROUPS_PARAM = 'cg';

function parseSortFromParams(params: URLSearchParams, defaultSort: SortState): SortState {
    const key = params.get(SORT_KEY_PARAM);
    const dir = params.get(SORT_DIR_PARAM);
    if (key && (dir === 'asc' || dir === 'desc')) return { key, direction: dir };
    return defaultSort;
}

function parseFiltersFromParams(params: URLSearchParams): FilterState {
    const state: FilterState = {};
    params.forEach((value, key) => {
        if (key.startsWith(FILTER_PREFIX)) {
            state[key.slice(FILTER_PREFIX.length)] = value;
        }
    });
    return state;
}

function parseCollapsedGroupsFromParams(params: URLSearchParams): Set<string> {
    const raw = params.get(COLLAPSED_GROUPS_PARAM);
    return raw ? new Set(raw.split(',').filter(Boolean)) : new Set();
}

export interface TableState<T> {
    sortState: SortState;
    filterState: FilterState;
    collapsedGroups: Set<string>;
    filteredRows: T[];
    toggleSort: (key: string) => void;
    setFilter: (key: string, value: string) => void;
    clearFilters: () => void;
    toggleGroup: (group: string) => void;
}

export function useTableState<T>(
    rows: T[],
    columns: Array<ColumnDef<T>>,
    filters: Array<FilterDef<T>>,
    defaultSort: SortState,
): TableState<T> {
    const [searchParams, setSearchParams] = useSearchParams();

    const sortState = useMemo(
        () => parseSortFromParams(searchParams, defaultSort),
        [searchParams, defaultSort],
    );
    const filterState = useMemo(() => parseFiltersFromParams(searchParams), [searchParams]);
    const collapsedGroups = useMemo(
        () => parseCollapsedGroupsFromParams(searchParams),
        [searchParams],
    );

    const filteredAndSorted = useMemo(() => {
        const filtered = filterRows(rows, filters, filterState);
        return sortRows(filtered, columns, sortState.key, sortState.direction);
    }, [rows, columns, filters, filterState, sortState]);

    const toggleSort = useCallback(
        (key: string) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    const currentKey = next.get(SORT_KEY_PARAM) || defaultSort.key;
                    const currentDir = next.get(SORT_DIR_PARAM) || defaultSort.direction;
                    if (currentKey === key) {
                        next.set(SORT_KEY_PARAM, key);
                        next.set(SORT_DIR_PARAM, currentDir === 'asc' ? 'desc' : 'asc');
                    } else {
                        next.set(SORT_KEY_PARAM, key);
                        next.set(SORT_DIR_PARAM, 'desc');
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams, defaultSort],
    );

    const setFilter = useCallback(
        (key: string, value: string) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (value) {
                        next.set(FILTER_PREFIX + key, value);
                    } else {
                        next.delete(FILTER_PREFIX + key);
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const toggleGroup = useCallback(
        (group: string) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    const current = parseCollapsedGroupsFromParams(next);
                    if (current.has(group)) current.delete(group);
                    else current.add(group);
                    if (current.size === 0) {
                        next.delete(COLLAPSED_GROUPS_PARAM);
                    } else {
                        next.set(COLLAPSED_GROUPS_PARAM, [...current].join(','));
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const clearFilters = useCallback(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                [...next.keys()].forEach((k) => {
                    if (k.startsWith(FILTER_PREFIX)) next.delete(k);
                });
                return next;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    return {
        sortState,
        filterState,
        collapsedGroups,
        filteredRows: filteredAndSorted,
        toggleSort,
        setFilter,
        clearFilters,
        toggleGroup,
    };
}
